import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
    type ConversationParticipation,
    getUpdatedReferralConversationCounts,
    type MappedConversation,
    type MappedMessage,
    type ReferralConversationCounts,
} from 'services/userSupportHelpers';

export interface UserSupportSliceState {
    socketConnected: boolean;
    connectionLoading: boolean;
    userProfile?: {
        upn: string;
        firstName?: string;
        lastName?: string;
    };
    referralConversations?: Record<string, MappedConversation>;
    currentReferral?: string;
    currentConversation?: string;
    conversationMessages: Record<string, { messages: Record<string, MappedMessage> }>;
    userParticipation?: Record<string, string | undefined>;
    conversationParticipation?: Record<string, ConversationParticipation>;
    unreadConversationCount?: number;
    referralConversationCounts?: ReferralConversationCounts;
    canLogWebsocketPerformance?: boolean;
}

const initialState: UserSupportSliceState = {
    socketConnected: false,
    connectionLoading: true,
    userProfile: undefined,
    referralConversations: undefined,
    currentReferral: undefined,
    currentConversation: undefined,
    conversationMessages: {},
    userParticipation: undefined,
    conversationParticipation: undefined,
    unreadConversationCount: undefined,
    referralConversationCounts: undefined,
    canLogWebsocketPerformance: undefined,
};

export const userSupportSlice = createSlice({
    name: 'userSupport',
    initialState,
    reducers: {
        setSocketConnected(state, action: PayloadAction<{ connected: boolean }>) {
            const { connected } = action.payload;
            return {
                ...state,
                socketConnected: connected,
            };
        },
        setConnectionLoading(state, action: PayloadAction<{ loading: boolean }>) {
            const { loading } = action.payload;
            return {
                ...state,
                connectionLoading: loading,
            };
        },
        updateUserProfile(
            state,
            action: PayloadAction<{
                upn: string;
                firstName?: string;
                lastName?: string;
            }>,
        ) {
            const { upn, firstName, lastName } = action.payload;
            return {
                ...state,
                userProfile: {
                    upn,
                    firstName,
                    lastName,
                },
            };
        },
        updateCurrentReferral(state, action: PayloadAction<{ referralId: string } | undefined>) {
            const { referralId } = action.payload ?? {};
            return { ...state, currentReferral: referralId };
        },
        updateReferralConversations(
            state,
            action: PayloadAction<{ conversations?: Record<string, MappedConversation> }>,
        ) {
            const { conversations } = action.payload;
            const { currentReferral } = state;
            if (!conversations) {
                return {
                    ...state,
                    referralConversations: undefined,
                };
            }
            const newReferral = Object.values(conversations)[0]?.referralId;
            const referralChange = newReferral !== currentReferral;
            return {
                ...state,
                currentReferral: newReferral,
                referralConversations: {
                    ...(referralChange ? {} : state.referralConversations),
                    ...conversations,
                },
            };
        },
        updateCurrentConversation(
            state,
            action: PayloadAction<{ conversationId: string } | undefined>,
        ) {
            const { conversationId } = action.payload ?? {};
            return {
                ...state,
                currentConversation: conversationId,
            };
        },
        updateConversationMessages(
            state,
            action: PayloadAction<{
                conversationId: string;
                messages: Record<string, MappedMessage>;
            }>,
        ) {
            const { conversationId, messages } = action.payload;
            return {
                ...state,
                currentConversation: conversationId,
                conversationMessages: {
                    ...state.conversationMessages,
                    [conversationId]: {
                        messages: {
                            ...(state.conversationMessages[conversationId]?.messages ?? {}),
                            ...messages,
                        },
                    },
                },
            };
        },
        updateUserParticipation(state, action: PayloadAction<Record<string, string | undefined>>) {
            const { userParticipation, conversationParticipation } = state;
            const userUpdates = action.payload;

            const updatedParticipation = {
                ...userParticipation,
                ...userUpdates,
            };

            const updatedConversationCounts = getUpdatedReferralConversationCounts(
                updatedParticipation,
                conversationParticipation,
            );

            return {
                ...state,
                userParticipation: updatedParticipation,
                referralConversationCounts: updatedConversationCounts,
                unreadConversationCount: Object.values(updatedConversationCounts ?? {}).reduce(
                    (prev, curr) => prev + curr.unread,
                    0,
                ),
            };
        },
        updateConversationParticipation(state, action: PayloadAction<ConversationParticipation[]>) {
            const { userParticipation, conversationParticipation } = state;
            const conversationUpdates = action.payload;

            const updatedParticipation = {
                ...conversationParticipation,
                ...Object.fromEntries(
                    conversationUpdates.map((conversation) => [
                        conversation.conversation_id,
                        conversation,
                    ]),
                ),
            };

            const updatedConversationCounts = getUpdatedReferralConversationCounts(
                userParticipation,
                updatedParticipation,
            );

            return {
                ...state,
                conversationParticipation: updatedParticipation,
                referralConversationCounts: updatedConversationCounts,
                unreadConversationCount: Object.values(updatedConversationCounts).reduce(
                    (prev, curr) => prev + curr.unread,
                    0,
                ),
            };
        },
        setCanLogWebsocketPerformance(state, action: PayloadAction<boolean>) {
            return { ...state, canLogWebsocketPerformance: action.payload };
        },
    },
});

export const {
    setSocketConnected,
    setConnectionLoading,
    updateUserProfile,
    updateCurrentReferral,
    updateReferralConversations,
    updateCurrentConversation,
    updateConversationMessages,
    updateUserParticipation,
    updateConversationParticipation,
    setCanLogWebsocketPerformance,
} = userSupportSlice.actions;
